export const AUDIO_GIFT_GUIDE = `
# Audio Gifts Guide

Send personalized wellness audio sessions as meaningful gifts to loved ones. This guide covers everything you need to know about creating and sharing Audio Gifts.

## What is an Audio Gift?

### A Personalized Wellness Experience

Audio Gifts are personalized meditation and wellness sessions you create for someone special. Unlike generic gift cards, Audio Gifts are:

- **Deeply personalized** - Tailored to the recipient's life, challenges, and aspirations
- **Emotionally meaningful** - Created with love and intention by you
- **Instantly deliverable** - Share via link, QR code, or social media
- **Unique** - No two Audio Gifts are the same

### Perfect For

- **Birthdays** - A personalized session celebrating their year ahead
- **Stressful times** - Comfort and support when they need it most
- **Celebrations** - Acknowledge achievements, graduations, promotions
- **Just because** - Show someone you're thinking of them
- **Holidays** - A meaningful alternative to material gifts
- **Grief support** - Gentle comfort during difficult times
- **New parents** - Relaxation sessions for exhausted caregivers
- **Students** - Focus and anxiety relief during exam periods

## How Audio Gifts Work

### The Power of Personalization

Audio Gifts use TWO biographies to create deeply personalized content:

1. **Recipient's Biography** - Selected from your Biography Library, containing their life details
2. **Your Personal Touch** - Your gift message and context about why you're sending this

The AI blends both to create a session that:
- Acknowledges you as the sender
- References the recipient's specific life details
- Addresses their actual challenges or celebrates their achievements
- Feels like it was created just for them (because it was!)

### What Makes It Special

**Without Personalization (Generic):**
> "Take a deep breath. You are valued. Someone cares about you."

**With Audio Gift Personalization:**
> "This session comes to you from Sarah, who wanted you to know she's been thinking about you. Michael, as you settle in, remember that summit you conquered at Silver Star Mountain last summer - that same strength is within you now. Sarah knows how hard you've been working at the clinic, and she wanted you to have this moment of peace..."

## Biography Library

Before creating Audio Gifts, you need to set up biographies for your loved ones.

### Setting Up Recipient Biographies

1. Navigate to **Biography Library** in the sidebar
2. Click **"Add New Biography"**
3. Enter the recipient's details:
   - **Name** - Their full name
   - **First Name** - What you call them
   - **Preferred Name** - Nickname or preferred form of address
   - **Biography** - Details about their life (see below)

### What to Include in Recipient Biographies

The more detail you provide, the more personalized the gift:

- **Life situation** - Job, family, living situation
- **Current challenges** - What they're dealing with (stress, health, transitions)
- **Relationships** - Partner, kids, pets, close friends
- **Achievements** - What they're proud of, past victories
- **Interests** - Hobbies, passions, what makes them happy
- **Values** - What matters most to them
- **Goals** - What they're working toward
- **Special memories** - Meaningful experiences you share

**Example Biography:**
> "My sister Maya lives in Boston with her husband David and their rescue dog Biscuit. She's a pediatric nurse working long hours at Children's Hospital. She's been stressed about balancing work with trying to get pregnant. She loves hiking, cooking elaborate meals, and her Sunday morning yoga practice. She's incredibly strong - she was the one who held our family together when Mom was sick. Her happy place is our family cabin in Vermont."

### Biography Library Limits

- **Default Biography** - Your main profile (1)
- **Loved Ones** - Up to 9 additional biographies
- **Total** - 10 biographies in your library

## Creating an Audio Gift

### Step-by-Step Guide

#### Step 1: Start Gift Creation

1. Click **"Send Audio Gift"** in the sidebar
2. You'll enter the 6-step gift creation wizard

#### Step 2: Select Recipient Biography

1. Choose from your Biography Library
2. If the recipient isn't in your library, click **"Add New Biography"** first
3. Review the biography details to ensure accuracy
4. The more complete the biography, the better the gift

#### Step 3: Choose Category

Select the type of session that fits the occasion:

- **Celebration** - Birthdays, achievements, milestones
- **Anxiety Relief** - Stress reduction and calm
- **Sleep** - Peaceful bedtime sessions
- **Meditation** - Mindfulness and presence
- **Motivation** - Energy and encouragement
- **Self-Love** - Compassion and appreciation
- **And more** - All VoiceThera categories are available

#### Step 4: Personalize the Session

Answer the questionnaire to customize the session:

**Questions are phrased for gift-givers:**
- "What is your loved one celebrating?"
- "Describe their current stress or challenges"
- "What do you want them to feel after this session?"
- "Is there anything special you'd like the AI to include?"

The more specific your answers, the more personalized the gift.

#### Step 5: Configure Session Details

- **Session Length** - 3, 5, 7, or 10 minutes
- **Voice Selection** - Choose a voice that fits the mood
- **Background Music** - Optional ambient music
- **Gift Message** - Add a personal note to the recipient
- **Sender Name** - How you want to be identified

#### Step 6: Generate & Share

1. Review your gift configuration
2. Confirm credit cost
3. Click **"Create Audio Gift"**
4. Wait for generation (30-90 seconds)
5. Get your shareable link

### Sharing Your Audio Gift

Once generated, share your gift via:

- **Copy Link** - Share anywhere via text, email, messaging
- **QR Code** - Generate a scannable code (great for cards)
- **WhatsApp** - Direct share to WhatsApp
- **SMS** - Text message sharing
- **Email** - Send via email link

## Credit Costs

### How Credits Work for Gifts

Audio Gifts use credits from your account at the same rate as personal sessions:

| Session Length | Credits Required |
|----------------|------------------|
| 3 minutes | 10 credits |
| 5 minutes | 17 credits |
| 7 minutes | 23 credits |
| 10 minutes | 33 credits |

### Gift Limits by Subscription

Different subscription tiers have different gift limits:

| Subscription | Monthly Gift Limit |
|--------------|-------------------|
| Free | 2 gifts/month |
| Basic | 10 gifts/month |
| Pro | Unlimited gifts |

**Note:** Purchased credits (not subscription credits) have no gift restrictions.

## What Recipients See

### The Gift Landing Page

When recipients open your gift link, they see a beautiful landing page with:

1. **Gift Card Display**
   - Your name as sender
   - Session title and category
   - Your personal message
   - Beautiful visual design

2. **Audio Player**
   - Full playback controls
   - Background music toggle
   - Download option (if enabled)

3. **Call to Action**
   - Invitation to create their own VoiceThera account
   - Sign up with referral credit (5 credits for both of you!)

### No Account Required

Recipients can play their Audio Gift immediately - no sign-up required. If they love it and create an account, you both receive bonus credits!

## Referral System

### Earn Credits from Gifts

When gift recipients sign up for VoiceThera:

- **You earn** 5 bonus credits
- **They receive** 5 bonus credits to start
- **Win-win** for meaningful sharing

This happens automatically when they create an account from your gift link.

## Tips for Meaningful Gifts

### 1. Choose the Right Category

Match the category to what they need:
- Stressed friend → Anxiety Relief or Meditation
- Birthday celebration → Celebration
- Trouble sleeping → Sleep
- New goal they're pursuing → Motivation

### 2. Complete Their Biography

More biography detail = more personalization:
- Include specific names (partner, kids, pets)
- Mention recent challenges or victories
- Reference places meaningful to them
- Note their interests and values

### 3. Make Your Message Personal

In the gift message field, write from the heart:
- Why you're sending this gift
- What they mean to you
- Specific words of encouragement
- Reference shared memories

### 4. Choose Appropriate Timing

Send gifts when they'll have time to enjoy them:
- Evening for sleep sessions
- Morning for motivation
- Weekend for longer sessions
- After a known stressful event

### 5. Consider Follow-Up

After sending, let them know to check their messages. A gift link buried in an inbox doesn't help anyone!

## Frequently Asked Questions

### Can I send multiple gifts to the same person?

Yes! Create new gifts anytime. Each will be unique based on the current questionnaire answers and any biography updates.

### Do gifts expire?

Gift links remain active for 1 year from creation. Recipients can access the audio anytime during this period.

### Can recipients download the audio?

Yes, recipients can download their Audio Gift for offline listening.

### What if I made a mistake?

Once generated, Audio Gifts cannot be edited. However, you can create a new gift with the correct information and share that link instead.

### Can I preview the gift before sending?

The generation process doesn't offer a preview. However, you can play the completed audio yourself before sharing the link.

### Do recipients see my biography?

No! Recipients only see the session content, your gift message, and your sender name. Your personal biography is never shared.

### Can I send gifts to myself?

Technically yes, but for personal sessions, just create a regular session instead - it's the same experience without the gift workflow.

### How do I know if they listened?

Your **My Gifts** dashboard (coming soon) will show engagement analytics including when gifts were opened and played.

### What voices are available?

All VoiceThera voices are available for gifts. Choose one that matches the mood:
- Warm and nurturing for comfort
- Energetic for motivation
- Calm and soothing for sleep/meditation

### Is there a character limit for gift messages?

Yes, gift messages have a 1000 character limit - plenty of space for a heartfelt note.

## Troubleshooting

### Gift Won't Generate

- **Check credits** - Ensure you have enough credits for the session length
- **Check gift limits** - Free tier users have 2 gifts/month limit
- **Biography complete** - Recipient biography should have enough detail

### Recipient Can't Open Link

- **Link copied correctly** - Ensure the full URL was shared
- **Link not expired** - Check if within 1 year of creation
- **Browser issues** - Try a different browser or device

### Session Doesn't Feel Personal

- **Enhance biography** - Add more specific details about the recipient
- **Answer questionnaire fully** - Provide detailed responses
- **Be specific** - Generic answers create generic sessions

## Ready to Send a Gift?

Creating an Audio Gift takes just 3-5 minutes, but the impact lasts much longer. Show someone you care with a truly personalized wellness experience.

**Start now:** Click "Send Audio Gift" in the sidebar to begin!
`;
